import { Download, Share, X } from 'lucide-react';
import React, { useState } from 'react';

/**
 * Professional Evaluation Modal Component
 * Based on conversation-mockup.html design
 * Displays detailed evaluation results for a candidate
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.candidate - Candidate with evaluation data
 * @param {string} props.jobTitle - Job title
 * @param {string} props.jobId - Job ID
 */
export function EvaluationModal({
  isOpen,
  onClose,
  candidate,
  jobTitle,
  jobId
}) {
  const [showFullSummary, setShowFullSummary] = useState(false);

  if (!isOpen || !candidate || !candidate.rating) return null;

  const getDisplayName = (candidate) => {
    return candidate.contactName || candidate.name || 'Unknown Candidate';
  };

  const getInitials = (name) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleDownload = () => {
    const evaluationData = {
      candidate: {
        name: getDisplayName(candidate),
        email: candidate.email,
        phoneNumbers: candidate.phoneNumbers || []
      },
      evaluation: {
        score: candidate.score,
        rating: candidate.rating,
        jobTitle: jobTitle,
        jobId: jobId,
        timestamp: new Date().toISOString()
      }
    };

    const blob = new Blob([JSON.stringify(evaluationData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evaluation_${getDisplayName(candidate).replace(/\s+/g, '_')}_${jobTitle?.replace(/\s+/g, '_') || 'job'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const truncateSummary = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Evaluation Results</h2>
              <p className="text-sm text-gray-600 mt-1">for: {jobTitle || 'Job Position'}</p>
              <p className="text-xs text-gray-500">Job ID: {jobId || 'N/A'}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-6">
          {/* Candidate Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">
                  {getInitials(getDisplayName(candidate))}
                </span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{getDisplayName(candidate)}</h3>
                <p className="text-lg text-gray-600">{candidate.title || 'No title'}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600 mb-1">
                Score: {candidate.score}%
              </div>
              {candidate.contactHistory?.contacted && (
                <div className="flex items-center gap-2 justify-end">
                  <span className="px-3 py-1 bg-yellow-200 text-yellow-900 text-xs font-medium rounded-full">
                    Previously Contacted
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Evaluation Summary */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Evaluation Summary:</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed text-sm">
                {showFullSummary
                  ? candidate.rating.explanation
                  : truncateSummary(candidate.rating.explanation, 200)}
              </p>
              {candidate.rating.explanation?.length > 200 && (
                <button
                  onClick={() => setShowFullSummary(!showFullSummary)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                >
                  {showFullSummary ? 'Show Less' : 'Show More'}
                </button>
              )}
            </div>
          </div>

          {/* Strengths Section */}
          {candidate.rating.strengths && candidate.rating.strengths.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Strengths:</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.rating.strengths.map((strength, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full"
                  >
                    {strength}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Areas for Improvement */}
          {candidate.rating.weaknesses && candidate.rating.weaknesses.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Areas for Improvement:</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.rating.weaknesses.map((weakness, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full"
                  >
                    {weakness}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Key Matches */}
          {candidate.rating.key_matches && candidate.rating.key_matches.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Matches:</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.rating.key_matches.map((match, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full"
                  >
                    {match}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Missing Skills */}
          {candidate.rating.missing_skills && candidate.rating.missing_skills.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Missing Skills:</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.rating.missing_skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Experience Gaps */}
          {candidate.rating.experience_gaps && candidate.rating.experience_gaps.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Experience Gaps:</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <ul className="space-y-1 text-sm text-gray-700">
                  {candidate.rating.experience_gaps.map((gap, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-yellow-600 mt-1">•</span>
                      <span>{gap}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {candidate.rating.recommendations && candidate.rating.recommendations.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Recommendations:</h4>
              <ul className="space-y-2">
                {candidate.rating.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1 font-bold">•</span>
                    <span className="text-gray-700 text-sm">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleDownload}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium text-sm flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Report
            </button>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium text-sm flex items-center gap-2">
              <Share className="w-4 h-4" />
              Share
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default EvaluationModal;

